import { isEmpty, isEqual, isNil, isNull } from "lodash";
import {
  ConfirmationStoreInfo,
  CSDict,
  IndividualHandlingConfirmationTargetRate,
  IndividualHandlingSearchRequest,
} from "../../features/api-interfaces";
import { BLANK_LABEL_VALUE, CODE_MASTER } from "../default-constants";
import { DEFAULT_COMMENT, REQUIRES_REP_CONFIRMATION } from "./constants";

/**
 * 画面表示用の確認結果を返却する.
 * @param {boolean | null} confirmationResult 確認結果
 * @returns {CSDict} 画面表示用の確認結果
 */
export const getConfirmationResultDict = (
  confirmationResult: boolean | null
): CSDict => {
  if (isNil(confirmationResult)) {
    return BLANK_LABEL_VALUE;
  }
  const CONFIRMATION_RESULT = CODE_MASTER["CONFIRMATION_RESULT"];
  return confirmationResult ? CONFIRMATION_RESULT[0] : CONFIRMATION_RESULT[1];
};

/**
 * 選択肢からAPI返却用の確認結果を返却する.
 * @param {string} confirmationResultValue 選択肢の確認結果のvalue
 * @returns {boolean | null} API返却用の確認結果
 */
export const getApiConfirmationResult = (
  confirmationResultValue: string
): boolean | null => {
  if (isEqual(confirmationResultValue, BLANK_LABEL_VALUE.value)) {
    return null;
  }
  return isEqual(
    confirmationResultValue,
    CODE_MASTER["CONFIRMATION_RESULT"][0].value
  );
};

/**
 * 確認結果を基にコメントを返却する.
 * 確認NGであればデフォルトコメントを返却する.
 * @param {string | undefined} confirmationResult 確認結果value
 * @param {string | null | undefined} comment コメント
 * @returns {string} コメント
 */
export const getComment = (
  confirmationResult: string | undefined,
  comment?: string | null
) => {
  const CONFIRMATION_RESULT = CODE_MASTER["CONFIRMATION_RESULT"];
  if (
    isEqual(confirmationResult, CONFIRMATION_RESULT[1].value) &&
    isEmpty(comment)
  ) {
    return DEFAULT_COMMENT;
  }
  return comment ?? "";
};

/**
 * APIから取得した料率カテゴリ一覧を区分連想配列に変換する.
 * @param {IndividualHandlingConfirmationTargetRate[] | undefined} targetRates
 * @returns {CSDict[]} 変換した料率カテゴリ一覧
 */
export const getTargetRateItems = (
  targetRates: IndividualHandlingConfirmationTargetRate[] | undefined
): CSDict[] => {
  if (targetRates !== undefined) {
    return targetRates.map((targetRates) => ({
      label: targetRates.categoryName,
      value: String(targetRates.categoryNo),
    }));
  }
  return [];
};

/**
 * 画面表示用の料率カテゴリコードを返却する.
 * @param {CSDict[]} rateCategoryItems 料率カテゴリ一覧
 * @param {number | null} destinationSpecificationCode APIから受け取った料率カテゴリコード
 * @returns {CSDict} 画面表示用の料率カテゴリコード
 */
export const getRateCategoryCodeDict = (
  rateCategoryItems: CSDict[],
  rateCategoryCode: number | null
) => {
  const rateCategory = rateCategoryItems.find((item) =>
    isEqual(item.value, String(rateCategoryCode))
  );
  return rateCategory ?? BLANK_LABEL_VALUE;
};

/**
 * 個別取扱確認結果一覧画面の以下の検索条件に絞り込んだ店舗一覧を返却する
 * ・個別取扱事由
 * ・ステータス
 * @param {ConfirmationStoreInfo[] | undefined} storeList
 * @param {IndividualHandlingSearchRequest} searchCriteria
 * @return {ExConfirmationStoreInfo[] | undefined}
 */
export const filteredStoreList = (
  storeList: ConfirmationStoreInfo[] | undefined,
  searchCriteria: IndividualHandlingSearchRequest
): ConfirmationStoreInfo[] | undefined => {
  const { individualHandlingReason, status } = searchCriteria;
  const CONFIRMATION_STATUS = CODE_MASTER["CONFIRMATION_STATUS"];
  return storeList?.filter((store) => {
    let matchesReason = true;
    let matchesStatus = true;
    if (!isEmpty(individualHandlingReason)) {
      matchesReason = isEqual(
        store.individualHandlingReason,
        individualHandlingReason
      );
    }
    if (!isEmpty(status)) {
      if (isEqual(status, CONFIRMATION_STATUS[0].value)) {
        matchesStatus = isNull(store.confirmationResult);
      } else if (isEqual(status, CONFIRMATION_STATUS[1].value)) {
        matchesStatus = !isNull(store.confirmationResult);
      }
    }
    return matchesReason && matchesStatus;
  });
};

/**
 * 要代表者確認かどうか判定する.
 * @param {string} individualHandlingReason 個別取扱事由
 * @returns {boolean} 要代表者確認が含まれているかどうか
 */
export const isRequiresRepNameConfirm = (
  individualHandlingReason: string
): boolean => {
  return individualHandlingReason
    .split(",")
    .includes(REQUIRES_REP_CONFIRMATION);
};

/**
 * 以下のkey情報を基に受け取った店舗情報の一致判定を行う.
 * ・申請ステータス番号
 * ・個別取扱事由
 * @param {ConfirmationStoreInfo} storeList 店舗情報
 * @param {number} applicationStatusNo 申請ステータス番号
 * @param {string} individualHandlingReason 個別取扱事由
 * @returns {boolean} true: 同一店舗情報である
 */
export const isSameStore = (
  store: ConfirmationStoreInfo,
  applicationStatusNo: number,
  individualHandlingReason: string
): boolean =>
  isEqual(store.applicationStatusNo, applicationStatusNo) &&
  isEqual(store.individualHandlingReason, individualHandlingReason);

/**
 * 引数で受け取った情報を基に確認結果が登録済みの店舗情報を返却する
 * @param {ConfirmationStoreInfo[] | undefined | null} storeList 店舗情報一覧
 * @param {number} applicationStatusNo 申請ステータス番号
 * @param {string} individualHandlingReason 個別取扱事由
 * @returns {ConfirmationStoreInfo | undefined} 店舗情報
 */
export const findRegisteredStore = (
  storeList: ConfirmationStoreInfo[] | undefined | null,
  applicationStatusNo: number,
  individualHandlingReason: string
): ConfirmationStoreInfo | undefined =>
  storeList?.find(
    (store) =>
      isSameStore(store, applicationStatusNo, individualHandlingReason) &&
      !isNull(store.confirmationResult)
  );
